// MonteCarloSimulation.js
import React, { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/* ----------------------------- Helpers -------------------------------- */

function mulberry32(seed) {
  // deterministische Zufallszahlen (für reproduzierbare Demos)
  let t = seed >>> 0;
  return function () {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function clampNumber(x, min, max) {
  if (!Number.isFinite(x)) return min;
  return Math.min(max, Math.max(min, x));
}

function formatEUR(value, digits = 0) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(value);
}

function formatEURShort(value) {
  const v = Math.max(0, value);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} Mio. €`;
  if (v >= 1_000) return `${Math.round(v / 1_000)} Tsd. €`;
  return `${Math.round(v)} €`;
}

function minMax(arr) {
  let min = Infinity;
  let max = -Infinity;
  for (const v of arr) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!arr.length) return { min: 0, max: 0 };
  return { min, max };
}

function percentile(sortedAsc, p) {
  if (!sortedAsc.length) return 0;
  const pp = clampNumber(p, 0, 1);
  const idx = (sortedAsc.length - 1) * pp;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedAsc[lo];
  const w = idx - lo;
  return sortedAsc[lo] * (1 - w) + sortedAsc[hi] * w;
}

/**
 * Histogramm (mit extra "0 €"-Bucket)
 * Das ist hilfreich, weil bei kleinen Wahrscheinlichkeiten sehr viele Simulationen 0 ergeben.
 */
function buildHistogramWithZeroBucket(values, binCount = 18) {
  const n = values.length;
  if (!n) return { labels: [], counts: [] };

  let zeros = 0;
  let max = 0;
  for (const v of values) {
    if (v === 0) zeros += 1;
    if (v > max) max = v;
  }

  // Alles 0
  if (max <= 0) {
    return { labels: ["0 €"], counts: [n] };
  }

  const bins = clampNumber(binCount, 6, 40);
  const positiveBins = bins - 1;
  const width = max / positiveBins;

  const counts = Array(bins).fill(0);
  counts[0] = zeros;

  for (const v of values) {
    if (v <= 0) continue;
    const idx0 = Math.min(positiveBins - 1, Math.floor((v - 1e-9) / width));
    const idx = 1 + idx0;
    counts[idx] += 1;
  }

  const labels = ["0 €"];
  for (let i = 0; i < positiveBins; i++) {
    const a = i * width;
    const b = (i + 1) * width;
    labels.push(`${formatEURShort(a)}–${formatEURShort(b)}`);
  }

  return { labels, counts };
}

/* --------------------------- Component -------------------------------- */

export default function MonteCarloSimulation() {
  // Eingaben (nah an deinem Text)
  const [iterations, setIterations] = useState(2000);
  const [months, setMonths] = useState(12);

  const [monthlyProbability, setMonthlyProbability] = useState(0.05); // 0.05 = 5%
  const [meanLoss, setMeanLoss] = useState(500000); // € pro erfolgreichem Ereignis
  const [variationPct, setVariationPct] = useState(20); // ±20%

  // Reproduzierbar für Unterricht/Demo
  const [useSeed, setUseSeed] = useState(true);
  const [seed, setSeed] = useState(12345);

 
  const [showHelp, setShowHelp] = useState(false);

  const [results, setResults] = useState([]);

  const params = useMemo(() => {
    const it = clampNumber(iterations, 200, 100000);
    const m = clampNumber(months, 1, 60);

    const p = clampNumber(monthlyProbability, 0, 1);
    const loss = Math.max(0, meanLoss);
    const varFrac = clampNumber(variationPct, 0, 100) / 100;

    return { it, m, p, loss, varFrac };
  }, [iterations, months, monthlyProbability, meanLoss, variationPct]);

  const theory = useMemo(() => {
    // Modellannahme: pro Monat gibt es entweder "Erfolg" oder "kein Erfolg" (max. 1 Erfolg/Monat)
    const pNoAttack = Math.pow(1 - params.p, params.m);
    const expectedAttacks = params.m * params.p;
    const expectedLoss = expectedAttacks * params.loss; // Schwankung hat im Mittel Faktor 1.0
    return { pNoAttack, expectedAttacks, expectedLoss };
  }, [params]);

  const runSimulation = () => {
    const rng = useSeed ? mulberry32(seed) : Math.random;

    const simulationResults = new Array(params.it);

    for (let i = 0; i < params.it; i++) {
      let totalLoss = 0;

      for (let month = 0; month < params.m; month++) {
        // Zufallsentscheidung: tritt ein erfolgreicher Angriff in diesem Monat auf?
        if (rng() < params.p) {
          // Schaden schwankt gleichmäßig im Bereich [1-var, 1+var]
          const factor = (1 - params.varFrac) + rng() * (2 * params.varFrac);
          totalLoss += params.loss * factor;
        }
      }

      simulationResults[i] = totalLoss;
    }

    setResults(simulationResults);
  };

  const stats = useMemo(() => {
    if (!results.length) return null;

    const { min, max } = minMax(results);
    const mean = results.reduce((acc, v) => acc + v, 0) / results.length;

    // Für p50/p95 etc. brauchen wir sortiert
    const sorted = [...results].sort((a, b) => a - b);
    const p50 = percentile(sorted, 0.5);
    const p90 = percentile(sorted, 0.9);
    const p95 = percentile(sorted, 0.95);
    const p99 = percentile(sorted, 0.99);

    const zeroCount = results.filter((v) => v === 0).length;
    const zeroPct = (zeroCount / results.length) * 100;

    return { mean, min, max, p50, p90, p95, p99, zeroCount, zeroPct };
  }, [results]);

  const hist = useMemo(() => {
    if (!results.length) return null;
    return buildHistogramWithZeroBucket(results, 18);
  }, [results]);

  const chartData = useMemo(() => {
    if (!hist) return null;
    return {
      labels: hist.labels,
      datasets: [
        {
          label: "Anzahl Simulationen",
          data: hist.counts,
        },
      ],
    };
  }, [hist]);

  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true },
        title: { display: true, text: "Verteilung der Jahresverluste (Histogramm)" },
        tooltip: { enabled: true },
      },
      scales: {
        y: { beginAtZero: true, title: { display: true, text: "Häufigkeit" } },
        x: {
          title: { display: true, text: "Jahresverlust (in Klassen)" },
          ticks: { maxRotation: 70, minRotation: 30 },
        },
      },
    }),
    []
  );

  return (
    <div className="container mt-4">
      <div className="card">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div>
              <h4 className="m-5">Monte‑Carlo Cyber‑Risiko Simulation (Phishing)</h4>
             
            </div>

        <button
  type="button"
  className="btn btn-outline-secondary"
  onClick={() => setShowHelp((v) => !v)}
>
  {showHelp ? "Erklärung ausblenden" : "Erklärung einblenden"}
</button>
          </div>

          {showHelp && (
            <div className="alert alert-info mt-3" style={{ marginBottom: 10 }}>
              <div style={{ textAlign: "left", fontWeight: 800, marginBottom: 6 }}>Was passiert in der Simulation?</div>
              <ol style={{ textAlign: "left", marginBottom: 0 }}>
                <li>
                  Ein Simulationslauf entspricht <strong>{params.m}</strong> Monaten (z. B. 12).
                </li>
                <li>
                  Pro Monat wird entschieden, ob ein erfolgreicher Angriff passiert (Wahrscheinlichkeit{" "}
                  <strong>{(params.p * 100).toFixed(1)}%</strong>).
                </li>
                <li>
                  Wenn ja, wird ein Schaden addiert: Ø <strong>{formatEUR(params.loss, 0)}</strong>, mit{" "}
                  <strong>±{variationPct}%</strong> Schwankung.
                </li>
                <li>
                  Das wird <strong>{params.it}</strong> Mal wiederholt. Ergebnis ist eine{" "}
                  <strong>Verteilung</strong> – kein Trend über die Zeit.
                </li>
              </ol>
            </div>
          )}

          {/* Inputs */}
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Anzahl Simulationen</label>
              <input
                type="number"
                className="form-control"
                value={iterations}
                onChange={(e) => setIterations(Number(e.target.value))}
                min={200}
                max={100000}
              />
              {showHelp && (
                <div style={{ opacity: 0.7, fontSize: 12, marginTop: 4 }}>
                  Empfehlung: 1.000–10.000.
                </div>
              )}
            </div>

            <div className="col-md-3">
              <label className="form-label">Zeitraum (Monate)</label>
              <input
                type="number"
                className="form-control"
                value={months}
                onChange={(e) => setMonths(Number(e.target.value))}
                min={1}
                max={60}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Wahrscheinlichkeit pro Monat</label>
              <input
                type="number"
                step="0.001"
                className="form-control"
                value={monthlyProbability}
                onChange={(e) => setMonthlyProbability(Number(e.target.value))}
                min={0}
                max={1}
              />
              {showHelp && (
                <div style={{ opacity: 0.7, fontSize: 12, marginTop: 4 }}>
                  Beispiel: <strong>0.05</strong> entspricht <strong>5%</strong>.
                </div>
              )}
            </div>

            <div className="col-md-3">
              <label className="form-label">Ø Schaden pro Erfolg (€)</label>
              <input
                type="number"
                className="form-control"
                value={meanLoss}
                onChange={(e) => setMeanLoss(Number(e.target.value))}
                min={0}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Variation (± in %)</label>
              <input
                type="number"
                className="form-control"
                value={variationPct}
                onChange={(e) => setVariationPct(Number(e.target.value))}
                min={0}
                max={100}
              />
              {showHelp && (
                <div style={{ opacity: 0.7, fontSize: 12, marginTop: 4 }}>
                  ±20% heißt: jeder Erfolg liegt zwischen 80% und 120% vom Ø‑Schaden.
                </div>
              )}
            </div>

            <div className="col-md-3 d-flex align-items-end">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={useSeed}
                  onChange={(e) => setUseSeed(e.target.checked)}
                  id="seedToggle"
                />
                <label className="form-check-label" htmlFor="seedToggle">
                  Reproduzierbar (Zufallsstartwert)
                </label>
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label">Zufallsstartwert</label>
              <input
                type="number"
                className="form-control"
                value={seed}
                onChange={(e) => setSeed(Number(e.target.value))}
                disabled={!useSeed}
              />
            </div>

            <div className="col-md-3 d-flex align-items-end">
              <button className="btn btn-primary w-100" onClick={runSimulation}>
                Simulation starten
              </button>
            </div>
          </div>

          <hr />

          {/* Theorie-Kasten */}
          <div className="row g-3">
            <div className="col-md-12">
              <div className="p-3 border rounded" style={{ background: "rgba(0,0,0,.02)" }}>
                <div style={{ fontWeight: 800 }}>Einordnung (aus den Parametern)</div>

                <div className="row g-3" style={{ marginTop: 6 }}>
                  <div className="col-md-4">
                    <div>Chance für <strong>0 Ereignisse</strong> im Zeitraum:</div>
                    <div style={{ fontWeight: 900, fontSize: 18 }}>
                      {(theory.pNoAttack * 100).toFixed(1)}%
                    </div>
                    {showHelp && (
                      <div style={{ opacity: 0.8, fontSize: 13, marginTop: 6 }}>
                        Das erklärt, warum in vielen Simulationen <strong>0 €</strong> herauskommt.
                      </div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <div>Erwartete Ereignisse:</div>
                    <div style={{ fontWeight: 900, fontSize: 18 }}>
                      {theory.expectedAttacks.toFixed(2)}
                    </div>
                    {showHelp && (
                      <div style={{ opacity: 0.8, fontSize: 13, marginTop: 6 }}>
                        „Erwartet“ heißt: im Durchschnitt über sehr viele Jahre.
                      </div>
                    )}
                  </div>

                  <div className="col-md-4">
                    <div>Grob erwarteter Verlust (Mittelwert):</div>
                    <div style={{ fontWeight: 900, fontSize: 18 }}>
                      {formatEUR(theory.expectedLoss, 0)}
                    </div>
                    {showHelp && (
                      <div style={{ opacity: 0.8, fontSize: 13, marginTop: 6 }}>
                        Der Mittelwert kann „hoch“ wirken, obwohl viele Jahre 0 € sind (weil wenige Jahre sehr teuer sind).
                      </div>
                    )}
                  </div>
                </div>

                {showHelp && (
                  <div style={{ opacity: 0.8, fontSize: 13, marginTop: 10 }}>
                    Modellannahme hier: <strong>maximal ein</strong> erfolgreicher Angriff pro Monat (ja/nein).
                    Für Unterricht ist das oft ausreichend und leicht erklärbar.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Ergebnisse */}
          {results.length > 0 && stats && (
            <div className="mt-3">
              <div className="p-3 border rounded" style={{ background: "rgba(0,0,0,.02)" }}>
                <div style={{ fontWeight: 800 }}>Simulationsergebnisse</div>

                <div className="row g-3" style={{ marginTop: 6 }}>
                  <div className="col-md-4">
                    Ø Verlust: <strong>{formatEUR(stats.mean, 0)}</strong>
                    <div style={{ opacity: 0.8, marginTop: 6 }}>
                      0‑Verlust‑Fälle: <strong>{stats.zeroCount}</strong> ({stats.zeroPct.toFixed(1)}%)
                    </div>
                    {showHelp && (
                      <div style={{ opacity: 0.8, fontSize: 13, marginTop: 6 }}>
                        Viele 0‑Fälle sind normal, wenn p klein ist.
                      </div>
                    )}
                  </div>

                  <div className="col-md-4">
                    Min / Max:{" "}
                    <strong>
                      {formatEUR(stats.min, 0)} / {formatEUR(stats.max, 0)}
                    </strong>
                    {showHelp && (
                      <div style={{ opacity: 0.8, fontSize: 13, marginTop: 6 }}>
                        „Max“ ist oft ein Zufalls‑Extrem. Für Entscheidungen sind p95/p99 meist hilfreicher.
                      </div>
                    )}
                  </div>

                  <div className="col-md-4">
                    p50 / p95 / p99:{" "}
                    <strong>
                      {formatEUR(stats.p50, 0)} / {formatEUR(stats.p95, 0)} / {formatEUR(stats.p99, 0)}
                    </strong>
                    {showHelp && (
                      <div style={{ opacity: 0.8, fontSize: 13, marginTop: 6 }}>
                        p95 heißt: 95% der Simulationen liegen <strong>unter</strong> diesem Verlust (5% darüber).
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Histogramm */}
          {results.length > 0 && chartData && (
            <div style={{ height: 430, marginTop: 14 }}>
              <Bar data={chartData} options={chartOptions} />
              {showHelp && (
                <div style={{ opacity: 0.8, fontSize: 13, marginTop: 8 }}>
                  Wichtig: Die x‑Achse ist <strong>kein Zeitverlauf</strong>, sondern Verlust‑Klassen.
                  Ein Histogramm ist hier verständlicher als eine Linie, weil wir eine Verteilung betrachten.
                </div>
              )}
            </div>
          )}

          {/* Mini‑Legende */}
          <div style={{ marginTop: 14, textAlign: "left", borderTop: "1px dashed rgba(0,0,0,.2)", paddingTop: 10 }}>
            <details>
              <summary style={{ cursor: "pointer", fontWeight: 800 }}>Mini‑Legende</summary>
              <ul style={{ marginTop: 10 }}>
                <li>
                  <strong>Iteration</strong>: ein kompletter Simulationslauf (hier: {params.m} Monate).
                </li>
                <li>
                  <strong>Erfolg pro Monat</strong>: in diesem Modell „ja/nein“ (max. 1 Erfolg pro Monat).
                </li>
                <li>
                  <strong>Histogramm</strong>: zeigt, wie häufig Verlustbereiche vorkommen (Verteilung).
                </li>
                <li>
                  <strong>p50</strong> (Median): 50% der Ergebnisse liegen darunter.
                </li>
                <li>
                  <strong>p95 / p99</strong>: 95% / 99% der Ergebnisse liegen darunter (gute „Worst‑Case‑Orientierung“).
                </li>
                
              </ul>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
}